import React, { memo } from "react";
import { useTranslate } from "helpers";
import { UserAccountStepProps } from "./types";
import { TextField } from "ui/components/TextField/TextField";
import { PasswordField } from "ui/components/TextField/PasswordField";
import { MultiDropdown } from "ui/components/Dropdown/MultiDropdown";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import {
  useHandleTextFieldChange,
  useHandleDropdownChange,
  useHandleMultiDropdownChange,
} from "services/forms/hooks";
import { USERNAME_EXISTS_ERROR } from "ui/modules/users/pages/UserDetailPage/components/UserDetailCard/components/UserEditCard/validation";

const UserAccountStep: React.FC<UserAccountStepProps> = ({
  formValues,
  setFormValues,
  errors,
  roleOptions,
  officeOptions,
  isEdit,
  officeLocked,
}) => {
  const { t } = useTranslate();

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const handleMultiDropdownChange = useHandleMultiDropdownChange(setFormValues, formValues);

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
          Korisnički podaci
        </h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Unesite podatke za prijavu, lozinku i ulogu korisnika.
        </p>
        <div className="border border-slate-200">
          {/* Prijava */}
          <div className="border-b border-slate-200 py-6 px-4">
            <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  Podaci za prijavu
                </h3>
                <p className="text-xs text-slate-500">
                  Korisničko ime i lozinka za prijavu u sustav
                </p>
            </div>
            <div className="mb-3">
              <TextField
                type="text"
                name="userName"
                label={t("admin.user.edit.username")}
                placeholder={t("admin.user.edit.username.placeholder")}
                onChange={handleTextFieldChange}
                value={formValues.userName}
                error={errors.userName}
                forceShowError={!!errors.userName?.startsWith(USERNAME_EXISTS_ERROR)}
                required
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <PasswordField
                  name="password"
                  label={t("admin.user.edit.password")}
                  placeholder={t("admin.user.edit.password.placeholder")}
                  onChange={handleTextFieldChange}
                  value={formValues.password}
                  error={errors.password}
                  required={!isEdit}
                />
              </div>
              <div className="flex-1">
                <PasswordField
                  name="confirmPassword"
                  label="Ponovi lozinku"
                  placeholder="Ponovite lozinku"
                  onChange={handleTextFieldChange}
                  value={formValues.confirmPassword}
                  error={errors.confirmPassword}
                  required={!isEdit}
                />
              </div>
            </div>
          </div>

          {/* Uloga i poslovnica */}
          <div className="py-6 px-4">
            <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                  Uloga i poslovnica
                </h3>
                <p className="text-xs text-slate-500">
                  Odaberite uloge i poslovnicu kojoj korisnik pripada
                </p>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <MultiDropdown
                  name="roles"
                  label={t("admin.user.edit.roles")}
                  placeholder={t("admin.user.edit.roles.placeholder")}
                  value={formValues.roles}
                  onChange={handleMultiDropdownChange}
                  options={roleOptions}
                  error={errors.roles}
                  required
                />
              </div>
              <div className="flex-1">
                <Dropdown
                  name="officeId"
                  label="Poslovnica"
                  placeholder="Odaberite poslovnicu"
                  value={formValues.officeId}
                  onChange={handleDropdownChange}
                  options={officeOptions}
                  error={errors.officeId}
                  disabled={officeLocked}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(UserAccountStep);
